import _ from 'lodash';
import React, {FC, useEffect, useState} from 'react';
import styled from 'styled-components';

import {CrmSyncErrorsBannerContainer} from '../../../../../../../../src/components/accounts/crm/banners/crmSyncErrorsBannerContainer';
import {CrmToFrontCustomFieldMapping} from '../../../../../../../../src/components/accounts/crm/common/crmSyncHelpers';
import {CrmMetadataModel} from '../../../../../../../../src/models/api/crmMetadataModel';
import {
  CrmSyncIntegrationResourceTypesEnum,
  CrmSyncMetadataFilterModel,
  CrmSyncModel,
  CrmSyncSourcesEnum,
  CrmSyncStatusesEnum,
} from '../../../../../../../../src/models/api/crmSyncModel';
import {ContactsManagerPanelId} from '../../../../../../../../src/store/panels/panelStateBase';
import {Trans} from '../../../../../../../shared/helpers/src/internationalization/react';
import {usePrevious} from '../../../../../../../shared/helpers/src/react/hooks/hookHelpers';
import {Handler, HandlerOf} from '../../../../../../../shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../../../shared/helpers/src/types/asyncStatuses';
import {Icon} from '../../../../../../../shared/ui/src/components/icon/icon';
import {fontSizes, fontWeights} from '../../../../../../../shared/ui/src/styles/fontStyles';
import {UserTheme} from '../../../../../../../shared/ui/src/theme/themeTypes';
import {useUserTheme} from '../../../../../../../shared/ui/src/theme/userThemeProvider';
import {CrmSyncsDomainFilter} from './crmSyncDomainFilter';
import {useCrmFields, useMetadata, useSearchLists} from './crmSyncsHooks';
import {CrmSyncsMappingContainer} from './crmSyncsMapping/crmSyncsMappingContainer';
import {CrmSyncOptionsEnum, EXTERNAL_LINK_SYNC_ALL, SelectedOption} from './crmSyncsOptions/crmSyncsOptions';
import {CrmSyncsOptionsContainer} from './crmSyncsOptions/crmSyncsOptionsContainer';
import {CrmSyncsPageResourceConfig, useCrmPageConfig} from './crmSyncsPageConfig/crmSyncsPageConfig';
import {CrmSyncsSectionHeader} from './crmSyncsSectionHeader';
import {CrmSyncsSkipNameMatchTooltip} from './crmSyncsSkipNameMatchTooltip';

/*
 * Types.
 */

export interface CrmSyncsFormModel {
  isEnabled: boolean;
  selectedListOption: SelectedOption;
  mappedFields: ReadonlyArray<CrmToFrontCustomFieldMapping>;
  isInvalid: boolean;
  shouldSkipNameMatch?: boolean;
  filter?: CrmSyncMetadataFilterModel;
}

/*
 * Props.
 */

export interface CrmSyncsFormProps {
  panelId: ContactsManagerPanelId;
  source: CrmSyncSourcesEnum;
  isConnected: boolean;
  hasExpiredCredentials: boolean;
  crmSync: CrmSyncModel;
  config: CrmSyncsPageResourceConfig;
  isAdmin: boolean;
  isSavingOrSyncing: boolean;
  isLastForm: boolean;
  initialFormState?: CrmSyncsFormModel;
  onViewErrorsClick: HandlerOf<CrmSyncIntegrationResourceTypesEnum>;
  resourceType: CrmSyncIntegrationResourceTypesEnum;
  disabledOptionType?: CrmSyncOptionsEnum;
  onChange: HandlerOf<CrmSyncsFormModel>;
  setHasExpiredCredentials: HandlerOf<boolean>;
}

/*
 * Styles.
 */

export const SectionDividerDiv = styled.div`
  height: 1px;
  width: 600px;
  background-color: #e6e6e6;
`;

const ErrorsBannerWrapperDiv = styled.div`
  margin-top: 20px;
  width: 600px;
`;

const StyledCrmSyncsMappingWrapperDiv = styled.div<{$hasMarginTop?: boolean}>`
  margin-top: ${(p) => (p.$hasMarginTop ? '8px' : 0)};
`;

const StyledBannerWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  flex-wrap: wrap;
  gap: 10px;
  background: ${(p) => p.theme.palette.orange.shade20};
  border-radius: 8px;
  padding: 9px 12px;
`;

const StyledBannerTextWrapperDiv = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  line-height: 16px;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.medium};
`;

/*
 * Component.
 */

export const CrmSyncsForm: FC<CrmSyncsFormProps> = (props) => {
  const {
    source,
    isConnected,
    panelId,
    crmSync,
    hasExpiredCredentials,
    config,
    isAdmin,
    isSavingOrSyncing,
    isLastForm,
    disabledOptionType,
    resourceType,
    initialFormState,
    onViewErrorsClick,
    onChange,
    setHasExpiredCredentials,
  } = props;
  const theme = useUserTheme();
  const {displayName, card, account} = useCrmPageConfig(source);
  const initialSelectedListOption = computeInitialListOption(crmSync, config);
  const [formState, setFormState] = useState<CrmSyncsFormModel>({
    isEnabled: crmSync.status === CrmSyncStatusesEnum.ENABLED,
    selectedListOption: initialSelectedListOption,
    mappedFields: computeInitialMappedFields(crmSync, config, initialSelectedListOption),
    isInvalid: false,
    shouldSkipNameMatch: crmSync.metadata.skipNameMatch,
    filter: crmSync.metadata.filter,
    ...initialFormState,
  });

  const isSelectedOptionDisabled =
    Boolean(disabledOptionType) && formState.selectedListOption.type === disabledOptionType;

  const isValidConnected = !hasExpiredCredentials && isConnected;
  const isSectionEnabled = formState.isEnabled && isValidConnected;
  const previousIsValidConnected = usePrevious(isValidConnected);

  useEffect(() => {
    const hasCardAndAccountConfig = card && account;
    // Enable the form by default if there is only one kind of sync.
    if (!hasCardAndAccountConfig && previousIsValidConnected === false && isValidConnected === true) {
      setFormState({...formState, isEnabled: true});
      onChange({...formState, isEnabled: true});
    }
  }, [isValidConnected, setFormState, formState, previousIsValidConnected, onChange, card, account]);

  const {metadata, status: metadataStatus} = useMetadata(
    crmSync.id,
    isValidConnected,
    setHasExpiredCredentials,
  );
  const {hasNoListAccess, onSearchListsAsync} = useSearchLists(source, resourceType, isValidConnected);

  const isChangeDisabled = !isValidConnected || isSavingOrSyncing;

  const onFormChange = (updatedFormState: CrmSyncsFormModel) => {
    const isInvalid = computeIsInvalid(
      isChangeDisabled,
      hasNoListAccess,
      updatedFormState.isEnabled,
      metadata,
      config,
      isSelectedOptionDisabled,
      updatedFormState.selectedListOption,
    );

    const requiredFields = config.syncMapping.computeRequiredMapping(updatedFormState.selectedListOption);
    const mappedFields = filterRequiredFieldsFromMapping(requiredFields, updatedFormState.mappedFields);
    setFormState({...updatedFormState, mappedFields, isInvalid});
    onChange({...updatedFormState, mappedFields, isInvalid});
  };

  const {crmFields, status: fieldsStatus} = useCrmFields(
    crmSync.id,
    isValidConnected,
    setHasExpiredCredentials,
  );

  const isLoading =
    metadataStatus === AsyncStatusesEnum.LOADING || fieldsStatus === AsyncStatusesEnum.LOADING;

  const hasNoOptions = Object.keys(config.syncOptions).length === 0;

  return (
    <>
      <SectionDividerDiv />
      <CrmSyncsSectionHeader
        source={source}
        isAdmin={isAdmin}
        resourceType={resourceType}
        isEnabled={isSectionEnabled}
        isChangeDisabled={isChangeDisabled}
        onChange={(isEnabled) => {
          onFormChange({...formState, isEnabled});
        }}
      />
      {maybeRenderErrorsBanner(
        crmSync,
        isSectionEnabled,
        isChangeDisabled,
        hasNoListAccess,
        displayName,
        theme,
        () => onViewErrorsClick(resourceType),
      )}
      {isSectionEnabled && (
        <>
          {source === CrmSyncSourcesEnum.OUTLOOK_DIRECTORY && (
            <CrmSyncsDomainFilter
              formState={formState}
              isDisabled={isChangeDisabled}
              onFormChange={onFormChange}
            />
          )}
          <CrmSyncsOptionsContainer
            isLoading={isLoading}
            isDisabled={isChangeDisabled || hasNoListAccess}
            source={source}
            onSearchListsAsync={onSearchListsAsync}
            resourceType={resourceType}
            allResourceCount={metadata?.crmResourceCount}
            selectedOption={formState.selectedListOption}
            disabledOptionType={disabledOptionType}
            onSelectOption={(newSelectedOption) => {
              onFormChange({...formState, selectedListOption: newSelectedOption});
            }}
          />
          <StyledCrmSyncsMappingWrapperDiv $hasMarginTop={hasNoOptions}>
            <CrmSyncsMappingContainer
              panelId={panelId}
              selectedOption={formState.selectedListOption}
              crmSyncId={crmSync?.id}
              source={source}
              resourceType={resourceType}
              isDisabled={computeIsInvalid(
                isChangeDisabled,
                hasNoListAccess,
                isSectionEnabled,
                metadata,
                config,
                isSelectedOptionDisabled,
                formState.selectedListOption,
              )}
              mappedFields={formState.mappedFields}
              crmFields={crmFields}
              crmMetadata={metadata}
              crmFilter={formState.filter}
              setMappedFields={(newMappedFields) => {
                onFormChange({...formState, mappedFields: newMappedFields});
              }}
              isLoading={isLoading}
            />
          </StyledCrmSyncsMappingWrapperDiv>
          <CrmSyncsSkipNameMatchTooltip
            resourceType={resourceType}
            shouldSkipNameMatch={formState.shouldSkipNameMatch}
            onCheckboxChange={(newShouldSkipNameMatch) => {
              onFormChange({...formState, shouldSkipNameMatch: newShouldSkipNameMatch});
            }}
          />
        </>
      )}
      {isLastForm && !isSectionEnabled && <SectionDividerDiv />}
    </>
  );
};

/*
 * Helpers.
 */

function computeHasAllMaxCountError(
  config: CrmSyncsPageResourceConfig,
  crmResourceCount?: number,
  selectedOptionType?: CrmSyncOptionsEnum,
) {
  return Boolean(
    crmResourceCount &&
      crmResourceCount > config.maxNumberToSync &&
      selectedOptionType === CrmSyncOptionsEnum.ALL,
  );
}

function computeIsInvalid(
  isChangeDisabled: boolean,
  hasNoListAccess: boolean,
  isSectionEnabled: boolean,
  metadata: CrmMetadataModel | undefined,
  config: CrmSyncsPageResourceConfig,
  isSelectedOptionDisabled: boolean,
  selectedOption?: SelectedOption,
) {
  if (!isSectionEnabled) {
    return false;
  }
  const hasNoListForListOption = selectedOption?.type === CrmSyncOptionsEnum.LISTS && !selectedOption.listId;
  const hasMaxCountError = computeHasAllMaxCountError(
    config,
    metadata?.crmResourceCount,
    selectedOption?.type,
  );

  return Boolean(
    isChangeDisabled ||
      hasNoListAccess ||
      !selectedOption ||
      hasNoListForListOption ||
      isSelectedOptionDisabled ||
      hasMaxCountError,
  );
}

function computeInitialMappedFields(
  crmSync: CrmSyncModel,
  config: CrmSyncsPageResourceConfig,
  selectedOption?: SelectedOption,
) {
  if (Object.values(crmSync.mapping).length === 0) {
    // We return the optional default fields if there is not an existing mapping
    return config.syncMapping.computeOptionalMapping();
  }

  // Required fields are automatically added to the request (see useUpdateCrmSync)
  // and displayed in ui in a unique way so we do not need them here
  const requiredFields = config.syncMapping.computeRequiredMapping(selectedOption);
  const existingMapping = _(crmSync.mapping)
    .map((customFieldId, crmFieldId) => ({crmFieldId, customFieldId}))
    .value();
  return filterRequiredFieldsFromMapping(requiredFields, existingMapping);
}

function computeInitialListOption(crmSync: CrmSyncModel, config: CrmSyncsPageResourceConfig): SelectedOption {
  const {externalListId} = crmSync.metadata;

  if (externalListId === EXTERNAL_LINK_SYNC_ALL) {
    return {
      type: CrmSyncOptionsEnum.ALL,
    };
  }
  if (!externalListId) {
    const hasAccountOption = Boolean(config.syncOptions[CrmSyncOptionsEnum.ACCOUNTS]);

    return {
      type: hasAccountOption ? CrmSyncOptionsEnum.ACCOUNTS : CrmSyncOptionsEnum.ALL,
    };
  }
  return {
    type: CrmSyncOptionsEnum.LISTS,
    listId: externalListId,
  };
}

function maybeRenderErrorsBanner(
  crmSync: CrmSyncModel,
  isSectionEnabled: boolean,
  isChangeDisabled: boolean,
  hasNoListAccess: boolean,
  displayName: string,
  theme: UserTheme,
  onViewErrorsClick: Handler,
) {
  if (!isSectionEnabled) {
    return null;
  }

  if (hasNoListAccess) {
    return (
      <ErrorsBannerWrapperDiv>
        <StyledBannerWrapperDiv>
          <Icon name="warningFilledSmall" color={theme.palette.orange.shade50} />
          <StyledBannerTextWrapperDiv>
            <Trans id="crm-syncs-form-access-error-banner-text">
              We are unable to reach your {displayName} account. Please refresh your authentication by
              disconnecting your {displayName} account above and logging in again.
            </Trans>
          </StyledBannerTextWrapperDiv>
        </StyledBannerWrapperDiv>
      </ErrorsBannerWrapperDiv>
    );
  }

  return (
    <ErrorsBannerWrapperDiv>
      <CrmSyncErrorsBannerContainer
        isDisabled={isChangeDisabled}
        syncId={crmSync.id}
        onViewErrorsClick={onViewErrorsClick}
      />
    </ErrorsBannerWrapperDiv>
  );
}

function filterRequiredFieldsFromMapping(
  requiredFields: readonly CrmToFrontCustomFieldMapping[],
  existingMapping: readonly CrmToFrontCustomFieldMapping[],
) {
  return existingMapping.filter(
    (mapping) => !requiredFields.some((requiredMapping) => requiredMapping.crmFieldId === mapping.crmFieldId),
  );
}
